import type { KitFontId, ThemeTypeStyle } from '../releaseThemes';

import { fitTextSize, textWidthEm, wrapWords } from './kitFontMetrics';
import { STORY_SIZES, storyFormatOf, type StoryFormat, type StorySizes } from './storyFormats';
import {
  STORY_LOGO_MAX_HEIGHT,
  STORY_LOGO_MAX_WIDTH,
  storyCoverSizes,
  storyFrameOf,
  type StoryCoverSizes,
  type StoryFrame,
} from './storyFrames';
import type { KitStory, StoryStyle } from './storyTypes';
import { watermarkGap, watermarkLockup, type WatermarkLockup } from './watermark';

export { STORY_SIZES };

/** A box in the canvas of the format (1080 x 1920 for a Story, 1080 x 1350 for a 4:5 feed post). */
export interface StoryBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface TextPart {
  box: StoryBox;
  lines: string[];
  sizePx: number;
  /** The line height as a multiple of the size. */
  lineHeight: number;
  align: 'center' | 'left';
}

export type StoryItem =
  | { kind: 'logo'; box: StoryBox }
  | ({ kind: 'kicker' } & TextPart)
  | {
      kind: 'cover';
      /** The outer box of the cover frame (a mat, a plate or a tube included). */
      box: StoryBox;
      /** True for the small cover of the lyric and countdown rows. */
      small: boolean;
      /** True: a Retro sleeve that moved left, so its record ends inside the frame. */
      withRecord: boolean;
    }
  | ({ kind: 'hero' } & TextPart)
  | { kind: 'ornament'; box: StoryBox }
  | ({ kind: 'title' } & TextPart)
  | ({ kind: 'artist' } & TextPart)
  | ({ kind: 'dayLabel' } & TextPart)
  | ({ kind: 'when' } & TextPart)
  | ({ kind: 'pill' } & TextPart)
  | ({ kind: 'link' } & TextPart)
  /**
   * The Free watermark lockup (design w0, `story/watermark.ts`): the last item of the stack,
   * centred under the SmartLink line (owner answer 2026-10-08). The box is the lockup box.
   */
  | { kind: 'watermark'; box: StoryBox; lockup: WatermarkLockup };

export interface StoryLayoutInput {
  story: KitStory;
  themeId: string;
  /** How the Story draws the cover (`storyStyleOf` of the asset style). Absent: the cover card. */
  style?: StoryStyle;
  fonts: { display: KitFontId; text: KitFontId };
  typeStyle: { display: ThemeTypeStyle & { scale: number }; text: ThemeTypeStyle };
  /** Free plans: the watermark goes under the SmartLink line. Absent: no watermark. */
  watermark?: boolean;
}

export interface StoryLayout {
  items: StoryItem[];
  frame: StoryFrame;
  /** The format of the stack (`story.format`): its canvas, its safe column and its detail factor. */
  format: StoryFormat;
  /** The scale that the hero, the title and the cover got so that the stack fits (1 = the design sizes). */
  scale: number;
}

/** The line heights of the design: display text 0.92, body text 1.1, the lyric 1.04, the day number 0.86. */
export const STORY_LINE_HEIGHTS = { display: 0.92, text: 1.1, lyric: 1.04, number: 0.86, link: 1.25 } as const;
/** A text that does not fit its line shrinks to this share of its size, then wraps (`fitText.ts`). */
export const STORY_FIT_FLOOR = 0.7;
/** The Free watermark lockup of a format: a stack at the logo height of the format (design w0). Pure. */
export function storyWatermarkLockup(format: StoryFormat): WatermarkLockup {
  return watermarkLockup('stack', format.watermarkL);
}

/** The smallest scale of the hero, the title and the cover when the stack does not fit. */
const MIN_SCALE = 0.55;
/** The most lines of a lyric. */
const LYRIC_MAX_LINES = 5;

const round = (value: number): number => Math.round(value * 100) / 100;

interface Fonts {
  display: { id: KitFontId; style: ThemeTypeStyle & { scale: number } };
  text: { id: KitFontId; style: ThemeTypeStyle };
}

function widthPx(text: string, font: { id: KitFontId; style: ThemeTypeStyle }, sizePx: number): number {
  return textWidthEm(text, font.id, font.style) * sizePx;
}

/**
 * The fit rule of the design: a text that is wider than its line shrinks to a floor of 70 % of its
 * size, then wraps at the floor. A word that is still wider than the line shrinks until it fits:
 * the hero never wraps inside a word. Pure.
 */
export function fitStoryLine(
  text: string,
  font: { id: KitFontId; style: ThemeTypeStyle },
  sizePx: number,
  lineWidthPx: number,
  wrap: boolean
): { lines: string[]; sizePx: number } {
  const full = widthPx(text, font, sizePx);
  if (full <= lineWidthPx || full <= 0) {
    return { lines: [text], sizePx };
  }
  const oneLine = (sizePx * lineWidthPx) / full;
  if (!wrap || oneLine >= sizePx * STORY_FIT_FLOOR) {
    return { lines: [text], sizePx: Math.floor(oneLine * 100) / 100 };
  }
  const floor = sizePx * STORY_FIT_FLOOR;
  const lines = wrapWords(text, font.id, font.style, lineWidthPx / floor);
  const widest = Math.max(...lines.map((line) => widthPx(line, font, floor)));
  const size = widest > lineWidthPx ? (floor * lineWidthPx) / widest : floor;
  return { lines, sizePx: Math.floor(size * 100) / 100 };
}

/** A block of the safe column: its height and how to place its items at a top. */
interface Block {
  height: number;
  place: (top: number) => StoryItem[];
  /**
   * The CSS `margin-top: auto` of the hero of Cover ground and Type only: the free space of the
   * column is shared between this block and the CTA group, so the stack sits lower.
   */
  autoTop?: boolean;
}

interface Column {
  x: number;
  w: number;
}

/** Everything a block of one layout reads: the texts, the theme frame, the fonts and the format. */
interface Ctx {
  story: KitStory;
  themeId: string;
  frame: StoryFrame;
  fonts: Fonts;
  column: Column;
  style: StoryStyle;
  format: StoryFormat;
  sizes: Readonly<StorySizes>;
  cover: StoryCoverSizes;
  watermark: boolean;
}

/** A frame detail (a border, a padding, an ornament) in the format: the Story value times its factor. */
const unitPx = (format: StoryFormat, value: number): number => Math.round(value * format.unit);

function textBlock(
  kind: 'kicker' | 'hero' | 'title' | 'artist' | 'dayLabel' | 'when',
  fit: { lines: string[]; sizePx: number },
  lineHeight: number,
  column: Column,
  padY = 0
): Block {
  const height = fit.lines.length * fit.sizePx * lineHeight + padY;
  return {
    height,
    place: (top) => [
      {
        kind,
        box: { x: column.x, y: round(top), w: column.w, h: round(height) },
        lines: fit.lines,
        sizePx: fit.sizePx,
        lineHeight,
        align: 'center',
      },
    ],
  };
}

/**
 * The height of the cover frame: the mat of the Handmade photo is taller than its picture
 * (`.cov { padding: 20px 20px 72px }`, small `10px 10px 34px`, times the factor of the format).
 * Every other frame is square.
 */
export function coverFrameHeight(themeId: string, size: number, small: boolean, unit = 1): number {
  if (themeId !== 'handmade') {
    return size;
  }
  const pad = Math.round((small ? 10 : 20) * unit);
  const bottom = Math.round((small ? 34 : 72) * unit);
  // The photo is the width less the two side paddings; the mat adds the top and the bottom padding.
  return size - pad + bottom;
}

/** The kicker paddings: the Hype label (10 / 8 px) and the Neon tube (12 px and a 4 px border). */
function kickerPadY(frame: StoryFrame, format: StoryFormat): number {
  if (frame.kicker === 'label') {
    return unitPx(format, 18);
  }
  return frame.kicker === 'tube' ? unitPx(format, 32) : 0;
}

/** The ornament of an out-now file: the hairline (full width), the Hype bar, the Noir line. */
function ornamentSize(frame: StoryFrame, lineWidth: number, format: StoryFormat): { w: number; h: number } | null {
  switch (frame.ornament) {
    case 'hairline':
      return { w: lineWidth, h: 2 };
    case 'bar':
      return { w: unitPx(format, 300), h: unitPx(format, 20) };
    case 'line':
      return { w: unitPx(format, 160), h: 3 };
    case null:
      return null;
    default: {
      const exhaustive: never = frame.ornament;
      return exhaustive;
    }
  }
}

/** The cover row of the lyric and the countdown: the small cover, then the title and the artist. */
function idRow(ctx: Ctx): Block {
  const { story, frame, fonts, column, themeId, sizes, format } = ctx;
  // Type only has no cover at all: the title and the artist stand alone, centred.
  const withCover = ctx.style !== 'type';
  const coverSize = withCover ? ctx.cover.small : 0;
  const coverHeight = withCover ? coverFrameHeight(themeId, coverSize, true, format.unit) : 0;
  // The Hype sticker of a feed post keeps its hard shadow clear of the title (`marginBottom`).
  const rowGap = withCover ? sizes.rowGap + ctx.cover.marginBottom : 0;
  const textWidth = column.w - coverSize - rowGap;
  const titleFit = fitStoryLine(
    story.title,
    fonts.display,
    sizes.rowTitle * frame.factors.display * fonts.display.style.scale,
    textWidth,
    true
  );
  const artistFit = fitStoryLine(story.artist, fonts.text, sizes.artist * frame.factors.text, textWidth, false);
  const titleHeight = titleFit.lines.length * titleFit.sizePx * STORY_LINE_HEIGHTS.display;
  const artistHeight = artistFit.sizePx * STORY_LINE_HEIGHTS.text;
  const textHeight = titleHeight + sizes.rowTextGap + artistHeight;
  const textBlockWidth = Math.min(
    textWidth,
    Math.max(
      ...titleFit.lines.map((line) => widthPx(line, fonts.display, titleFit.sizePx)),
      widthPx(story.artist, fonts.text, artistFit.sizePx)
    )
  );
  const rowWidth = coverSize + rowGap + textBlockWidth;
  const left = column.x + (column.w - rowWidth) / 2;
  const height = Math.max(coverHeight, textHeight);
  const align = withCover ? 'left' : 'center';
  return {
    height,
    place: (top) => {
      const textTop = top + (height - textHeight) / 2;
      const textX = left + coverSize + rowGap;
      const cover: StoryItem[] = withCover
        ? [
            {
              kind: 'cover',
              box: { x: round(left), y: round(top + (height - coverHeight) / 2), w: coverSize, h: round(coverHeight) },
              small: true,
              withRecord: false,
            },
          ]
        : [];
      return [
        ...cover,
        {
          kind: 'title',
          box: { x: round(textX), y: round(textTop), w: round(textBlockWidth), h: round(titleHeight) },
          lines: titleFit.lines,
          sizePx: titleFit.sizePx,
          lineHeight: STORY_LINE_HEIGHTS.display,
          align,
        },
        {
          kind: 'artist',
          box: {
            x: round(textX),
            y: round(textTop + titleHeight + sizes.rowTextGap),
            w: round(textBlockWidth),
            h: round(artistHeight),
          },
          lines: [story.artist],
          sizePx: artistFit.sizePx,
          lineHeight: STORY_LINE_HEIGHTS.text,
          align,
        },
      ];
    },
  };
}

/** The large cover of a pre-save, an out-now and an announce file, with the space above and under it. */
function bigCover(ctx: Ctx, moment: 'preSave' | 'outNow' | 'announce', scale: number): Block {
  const { themeId, column, format } = ctx;
  const size = ctx.cover[moment] * scale;
  const height = coverFrameHeight(themeId, size, false, format.unit);
  const marginTop = ctx.cover.marginTop[moment];
  // Retro: the sleeve moves left by 24 % of its width, so the record ends inside the frame.
  const shift = themeId === 'retro' ? size * 0.24 : 0;
  return {
    height: height + marginTop + ctx.cover.marginBottom,
    place: (top) => [
      {
        kind: 'cover',
        box: {
          x: round(column.x + (column.w - size) / 2 - shift),
          y: round(top + marginTop),
          w: round(size),
          h: round(height),
        },
        small: false,
        withRecord: themeId === 'retro',
      },
    ],
  };
}

/** The title and the artist under a large cover, `idGap` apart. */
function idGroup(ctx: Ctx, scale: number, titleSize: number): Block {
  const { story, frame, fonts, column, sizes } = ctx;
  const titleFit = fitStoryLine(
    story.title,
    fonts.display,
    titleSize * frame.factors.display * fonts.display.style.scale * scale,
    column.w,
    true
  );
  const artistFit = fitStoryLine(story.artist, fonts.text, sizes.artist * frame.factors.text, column.w, false);
  const title = textBlock('title', titleFit, STORY_LINE_HEIGHTS.display, column);
  const artist = textBlock('artist', artistFit, STORY_LINE_HEIGHTS.text, column);
  return {
    height: title.height + sizes.idGap + artist.height,
    place: (top) => [...title.place(top), ...artist.place(top + title.height + sizes.idGap)],
  };
}

/** The line under the pill: the address, after "Link in bio" on a feed post. Null: no line. Pure. */
export function storyLinkLine(story: Pick<KitStory, 'link' | 'linkNote'>): string | null {
  if (story.link === null) {
    return null;
  }
  const note = story.linkNote ?? null;
  return note === null || note.trim() === '' ? story.link : `${note} · ${story.link}`;
}

/**
 * The text size of the CTA pill: the pill size of the format (the artist size of a Story and a feed
 * post) times the text factor of the theme. Handmade writes its own size with the marker font (54 px
 * on a Story), scaled to the format. Pure.
 */
function pillTextSize(frame: StoryFrame, format: StoryFormat, sizes: Readonly<StorySizes>): number {
  if (frame.pillTextPx === null) {
    return (sizes.pillText ?? sizes.artist) * frame.factors.text;
  }
  // A format with its own pill size (the lyric card: 32 px, Handmade 40) scales the Handmade size by it.
  return sizes.pillText === undefined
    ? unitPx(format, frame.pillTextPx)
    : Math.round((frame.pillTextPx * sizes.pillText) / STORY_SIZES.artist);
}

/**
 * The CTA group at the bottom of the safe column: the pill, the SmartLink line under it, and the
 * Free watermark under the line (owner answer 2026-10-08), each `pillLinkGap` apart.
 */
function ctaItems(ctx: Ctx): { top: number; items: StoryItem[] } {
  const { story, frame, fonts, column, sizes, format } = ctx;
  const pillFont = {
    id: fonts.text.id,
    style: { ...fonts.text.style, tracking: Math.min(fonts.text.style.tracking, 0.12) },
  };
  const pillText = fitStoryLine(
    story.cta,
    pillFont,
    pillTextSize(frame, format, sizes),
    column.w - 2 * sizes.pillPaddingX,
    false
  );
  const pillWidth = Math.min(column.w, widthPx(story.cta, pillFont, pillText.sizePx) + 2 * sizes.pillPaddingX);
  const items: StoryItem[] = [];
  let bottom = format.safeBottom + (ctx.watermark ? (format.watermarkRoom ?? 0) : 0);
  if (ctx.watermark) {
    const lockup = storyWatermarkLockup(format);
    items.unshift({
      kind: 'watermark',
      box: { x: round(column.x + (column.w - lockup.w) / 2), y: round(bottom - lockup.h), w: lockup.w, h: lockup.h },
      lockup,
    });
    bottom -= lockup.h + watermarkGap(format.watermarkL);
  }
  const line = storyLinkLine(story);
  if (line !== null) {
    const linkFont = { id: fonts.text.id, style: { ...fonts.text.style, case: 'none' as const, tracking: 0.02 } };
    const linkFit = fitStoryLine(line, linkFont, sizes.link * frame.factors.text, column.w, false);
    const linkHeight = linkFit.sizePx * STORY_LINE_HEIGHTS.link;
    items.unshift({
      kind: 'link',
      box: { x: column.x, y: round(bottom - linkHeight), w: column.w, h: round(linkHeight) },
      lines: [line],
      sizePx: linkFit.sizePx,
      lineHeight: STORY_LINE_HEIGHTS.link,
      align: 'center',
    });
    bottom -= linkHeight + sizes.pillLinkGap;
  }
  const pillTop = bottom - sizes.pillHeight;
  items.unshift({
    kind: 'pill',
    box: {
      x: round(column.x + (column.w - pillWidth) / 2),
      y: round(pillTop),
      w: round(pillWidth),
      h: sizes.pillHeight,
    },
    lines: [story.cta],
    sizePx: pillText.sizePx,
    lineHeight: 1,
    align: 'center',
  });
  return { top: pillTop, items };
}

function logoBlock(ctx: Ctx): Block[] {
  if (!ctx.story.withLogo) {
    return [];
  }
  return [
    {
      height: STORY_LOGO_MAX_HEIGHT,
      place: (top) => [
        {
          kind: 'logo',
          box: {
            x: (ctx.format.size.width - STORY_LOGO_MAX_WIDTH) / 2,
            y: round(top),
            w: STORY_LOGO_MAX_WIDTH,
            h: STORY_LOGO_MAX_HEIGHT,
          },
        },
      ],
    },
  ];
}

/** The ornament block between the hero word and the title of an out-now file. */
function ornamentBlocks(ctx: Ctx): Block[] {
  const { frame, column, format } = ctx;
  const ornament = ornamentSize(frame, column.w, format);
  if (ornament === null) {
    return [];
  }
  return [
    {
      height: ornament.h,
      place: (top) => [
        {
          kind: 'ornament',
          box: { x: round(column.x + (column.w - ornament.w) / 2), y: round(top), w: ornament.w, h: ornament.h },
        },
      ],
    },
  ];
}

/** The date ring: a 14 px padding and a 4 px border above and under the text (times the factor). */
function whenBlock(ctx: Ctx, text: string): Block {
  const { fonts, frame, column, sizes, format } = ctx;
  const fit = fitStoryLine(text, fonts.text, sizes.when * frame.factors.text, column.w - unitPx(format, 76), false);
  return textBlock('when', fit, STORY_LINE_HEIGHTS.text, column, unitPx(format, 36));
}

/** The blocks of the countdown: the day number, "days to go", the date ring, the cover row. */
function countdownBlocks(ctx: Ctx, scale: number, blocks: Block[]): Block[] {
  const { story, frame, fonts, column, style, sizes } = ctx;
  const displaySize = (base: number, factor: number): number => base * factor * fonts.display.style.scale * scale;
  if (story.hero !== null) {
    const fit = fitStoryLine(
      story.hero,
      fonts.display,
      displaySize(style === 'type' ? sizes.numberType : sizes.number, frame.factors.number),
      column.w,
      false
    );
    blocks.push(textBlock('hero', fit, STORY_LINE_HEIGHTS.number, column));
  }
  if (story.dayLabel !== null) {
    const fit = fitStoryLine(
      story.dayLabel,
      fonts.display,
      displaySize(sizes.dayLabel, frame.factors.display),
      column.w,
      true
    );
    blocks.push(textBlock('dayLabel', fit, STORY_LINE_HEIGHTS.display, column));
  }
  if (story.when !== null) {
    blocks.push(whenBlock(ctx, story.when));
  }
  blocks.push(idRow(ctx));
  return blocks;
}

/** The blocks of the top of the safe column at one scale (the lyric has its own layout). */
function stackBlocks(ctx: Ctx, scale: number): Block[] {
  const { story, frame, fonts, column, style, sizes } = ctx;
  const displaySize = (base: number, factor: number): number => base * factor * fonts.display.style.scale * scale;
  // Cover ground and Type only: no cover card in the hero; the type grows and sits lower.
  const textLed = style !== 'card';
  const blocks = logoBlock(ctx);
  const hero = (text: string, base: number): Block => ({
    ...textBlock(
      'hero',
      fitStoryLine(text, fonts.display, displaySize(base, frame.factors.display), column.w, true),
      STORY_LINE_HEIGHTS.display,
      column
    ),
    autoTop: textLed,
  });
  const titleSize = textLed ? sizes.titleLed : sizes.title;
  switch (story.moment) {
    case 'preSave':
      if (story.kicker !== null) {
        const fit = fitStoryLine(story.kicker, fonts.text, sizes.kicker * frame.factors.text, column.w, false);
        blocks.push(textBlock('kicker', fit, STORY_LINE_HEIGHTS.text, column, kickerPadY(frame, ctx.format)));
      }
      if (!textLed) {
        blocks.push(bigCover(ctx, 'preSave', scale));
      }
      if (story.hero !== null) {
        blocks.push(hero(story.hero, textLed ? sizes.heroDateLed : sizes.heroDate));
      }
      blocks.push(idGroup(ctx, scale, titleSize));
      return blocks;
    case 'outNow':
      if (!textLed) {
        blocks.push(bigCover(ctx, 'outNow', scale));
      }
      if (story.hero !== null) {
        blocks.push(hero(story.hero, textLed ? sizes.heroWordLed : sizes.heroWord));
      }
      blocks.push(...ornamentBlocks(ctx), idGroup(ctx, scale, titleSize));
      return blocks;
    case 'announce':
      // The feed post of design e: "New single", the date ring, the cover, the title and the artist.
      if (story.hero !== null) {
        blocks.push(hero(story.hero, textLed ? sizes.heroNewLed : sizes.heroNew));
      }
      if (story.when !== null) {
        blocks.push(whenBlock(ctx, story.when));
      }
      if (!textLed) {
        blocks.push(bigCover(ctx, 'announce', scale));
      }
      blocks.push(idGroup(ctx, scale, titleSize));
      return blocks;
    case 'countdown':
      return countdownBlocks(ctx, scale, blocks);
    case 'lyric':
      // The lyric has its own layout (`lyricLayout`); `storyLayout` never asks for its blocks.
      return blocks;
    default: {
      const exhaustive: never = story.moment;
      return exhaustive;
    }
  }
}

/** The height of the stack: the sum of the block heights and the gaps. */
function stackHeight(blocks: Block[], gap: number): number {
  return blocks.reduce((sum, block, index) => sum + block.height + (index < blocks.length - 1 ? gap : 0), 0);
}

/**
 * Places the blocks in the safe column. `free` is the space between the stack and the CTA group.
 * A Story starts at the top of the column: a block with `autoTop` takes half of the free space
 * above itself (the other half stays above the CTA), as the two `auto` margins of the design share
 * it. A feed post centres the whole stack in the free space (design e, `.fp-main`).
 */
function placeStack(blocks: Block[], gap: number, free: number, format: StoryFormat): StoryItem[] {
  let top = format.safeTop + (format.stackTopRoom ?? 0);
  if (format.stack === 'centre') {
    top += Math.max(0, free) / 2;
  }
  const items: StoryItem[] = [];
  const autoShare =
    format.stack === 'top' && blocks.some((block) => block.autoTop === true) ? Math.max(0, free) / 2 : 0;
  blocks.forEach((block, index) => {
    if (block.autoTop === true) {
      top += autoShare;
    }
    items.push(...block.place(top));
    top += block.height + (index < blocks.length - 1 ? gap : 0);
  });
  return items;
}

/**
 * The fit of a lyric of one or two lines (owner answer 2026-10-08: up to two consecutive lines,
 * stored with a line break). Each line keeps its own break. A line stays whole while it can shrink
 * to `STORY_FIT_FLOOR` of the wrapped size; else it wraps inside the column. The size is the largest
 * at which every line fits the width, the lines stay at most `LYRIC_MAX_LINES` and the block fits
 * the height. A lyric with no break is the plain `fitTextSize`. Pure.
 */
export function fitLyric(input: {
  text: string;
  font: { id: KitFontId; style: ThemeTypeStyle };
  widthPx: number;
  heightPx: number;
  maxSizePx: number;
  /** The line height of the text. Absent: the lyric of a Story (1.04). */
  lineHeight?: number;
  /** The most lines. Absent: the lyric of a Story (5). */
  maxLines?: number;
}): { lines: string[]; sizePx: number } {
  const lineHeight = input.lineHeight ?? STORY_LINE_HEIGHTS.lyric;
  const maxLines = input.maxLines ?? LYRIC_MAX_LINES;
  const parts = input.text
    .split(/\r?\n/)
    .map((part) => part.trim())
    .filter((part) => part !== '');
  if (parts.length <= 1) {
    const fit = fitTextSize({
      text: parts[0] ?? input.text,
      fontId: input.font.id,
      style: input.font.style,
      // `fitTextSize` keeps 92 % of the box: the box is the line width over that share.
      boxWidthPx: input.widthPx / 0.92,
      boxHeightPx: input.heightPx,
      lineHeight,
      maxSizePx: input.maxSizePx,
      maxLines,
    });
    return { lines: fit.lines, sizePx: fit.sizePx };
  }
  const layoutAt = (size: number, wrap: boolean): { lines: string[]; fits: boolean } => {
    const lines = wrap
      ? parts.flatMap((part) => wrapWords(part, input.font.id, input.font.style, input.widthPx / size))
      : parts;
    const widest = Math.max(...lines.map((line) => widthPx(line, input.font, size)));
    const height = lines.length * size * lineHeight;
    return {
      lines,
      fits: lines.length <= maxLines && widest <= input.widthPx + 0.01 && height <= input.heightPx + 0.01,
    };
  };
  const largestSize = (wrap: boolean): number => {
    let low = 8;
    let high = Math.max(low, input.maxSizePx);
    if (layoutAt(high, wrap).fits) {
      return high;
    }
    for (let step = 0; step < 32 && high - low > 0.05; step += 1) {
      const middle = (low + high) / 2;
      if (layoutAt(middle, wrap).fits) {
        low = middle;
      } else {
        high = middle;
      }
    }
    return low;
  };
  // Each line stays whole when that costs little: the size with the stored breaks only is at least
  // `STORY_FIT_FLOOR` of the size with the lines wrapped. Else a long line wraps in the column.
  const wrappedSize = largestSize(true);
  const wholeSize = largestSize(false);
  const wrap = wholeSize < wrappedSize * STORY_FIT_FLOOR;
  const sizePx = Math.floor((wrap ? wrappedSize : wholeSize) * 100) / 100;
  return { lines: layoutAt(sizePx, wrap).lines, sizePx };
}

/**
 * The lyric file: the lyric fills the free space of the safe column, the cover row under it, the
 * CTA at the bottom. A Story shares the free space in three (the three `auto` margins of design b);
 * a feed post keeps the cover row just above the CTA and centres the lyric in the space above it
 * (design e, `.lyric { margin: auto 0 }`).
 */
function lyricLayout(ctx: Ctx, ctaTop: number): StoryItem[] {
  const { story, frame, fonts, column, format, sizes } = ctx;
  const { gap } = sizes;
  const logo = logoBlock(ctx);
  const logoHeight = logo.reduce((sum, block) => sum + block.height + gap, 0);
  const row = idRow(ctx);
  const top = format.safeTop + logoHeight;
  const room = ctaTop - gap - row.height - gap - top;
  const lyricFormat = format.lyric;
  const lyricStyle = {
    ...fonts.display.style,
    tracking: frame.lyricTracking ?? fonts.display.style.tracking,
    // The lyric card quotes the lyric in the artist's own case in every theme (owner answer 3 of page k).
    ...(lyricFormat?.keepCase === true ? { case: 'none' as const } : {}),
  };
  const base = ctx.style === 'type' ? sizes.lyricType : sizes.lyric;
  const themeFactors = lyricFormat?.themeFactors ?? true;
  const fit = fitLyric({
    text: story.hero ?? story.title,
    font: { id: fonts.display.id, style: lyricStyle },
    widthPx: column.w,
    heightPx: Math.max(0, room),
    maxSizePx: base * (themeFactors ? frame.factors.display * frame.factors.lyric : 1) * fonts.display.style.scale,
    ...(lyricFormat === undefined ? {} : { maxLines: lyricFormat.maxLines }),
  });
  const lyricHeight = fit.lines.length * fit.sizePx * STORY_LINE_HEIGHTS.lyric;
  const free = Math.max(0, ctaTop - top - lyricHeight - row.height - 2 * gap);
  const centred = format.stack === 'centre';
  const lyricTop = top + (centred ? free / 2 : free / 3);
  const rowTop = centred ? ctaTop - gap - row.height : lyricTop + lyricHeight + gap + free / 3;
  const items: StoryItem[] = logo.flatMap((block) => block.place(format.safeTop));
  items.push({
    kind: 'hero',
    box: { x: column.x, y: round(lyricTop), w: column.w, h: round(lyricHeight) },
    lines: fit.lines,
    sizePx: fit.sizePx,
    lineHeight: STORY_LINE_HEIGHTS.lyric,
    align: 'center',
  });
  items.push(...row.place(rowTop));
  return items;
}

/** The cover sizes of a theme in a format, with the small cover of the format (the lyric card: 120 px). */
function coverSizesOf(frame: StoryFrame, format: StoryFormat): StoryCoverSizes {
  const sizes = scaledCoverSizes(storyCoverSizes(frame, format.id), format.coverScale ?? 1);
  return format.smallCover === undefined ? sizes : { ...sizes, small: format.smallCover };
}

/** The cover sizes times the cover factor of a format (a short video: 0.88), in whole px. */
function scaledCoverSizes(sizes: StoryCoverSizes, factor: number): StoryCoverSizes {
  if (factor === 1) {
    return sizes;
  }
  const px = (value: number): number => Math.round(value * factor);
  return {
    preSave: px(sizes.preSave),
    outNow: px(sizes.outNow),
    announce: px(sizes.announce),
    small: px(sizes.small),
    marginTop: {
      preSave: px(sizes.marginTop.preSave),
      outNow: px(sizes.marginTop.outNow),
      announce: px(sizes.marginTop.announce),
    },
    marginBottom: px(sizes.marginBottom),
  };
}

/** The gap of the stack of a moment: the countdown and the announce post are tighter. */
function stackGap(story: KitStory, sizes: Readonly<StorySizes>): number {
  if (story.moment === 'countdown') {
    return sizes.countdownGap;
  }
  return story.moment === 'announce' ? sizes.announceGap : sizes.gap;
}

/**
 * The layout of one Story or feed post (design `asset-redesign/b Story - new` and `e Feed post -
 * new`, "Build spec for the Remotion layouts"): a centred column in the safe band of the format
 * (x `marginX` to the width less `marginX`; a Story y 270 to 1560, a 4:5 feed post y 72 to 1278),
 * the CTA group at the bottom of the column. The logo, when the kit has one, goes first. When the
 * stack does not fit (a long title, the logo), the hero, the title and the cover shrink together,
 * down to 55 %. Every box is in the canvas of the format (`format.size`). Pure.
 */
export function storyLayout(input: StoryLayoutInput): StoryLayout {
  const frame = storyFrameOf(input.themeId);
  const format = storyFormatOf(input.story.format);
  const ctx: Ctx = {
    story: input.story,
    themeId: input.themeId,
    frame,
    fonts: {
      display: { id: input.fonts.display, style: input.typeStyle.display },
      text: { id: input.fonts.text, style: input.typeStyle.text },
    },
    column: {
      x: format.marginX ?? frame.marginX,
      w: format.size.width - 2 * (format.marginX ?? frame.marginX),
    },
    style: input.style ?? 'card',
    format,
    sizes: format.sizes,
    cover: coverSizesOf(frame, format),
    watermark: input.watermark === true,
  };
  const cta = ctaItems(ctx);
  if (input.story.moment === 'lyric') {
    return { items: [...lyricLayout(ctx, cta.top), ...cta.items], frame, format, scale: 1 };
  }
  const gap = stackGap(input.story, format.sizes);
  const fits = (scale: number): { items: StoryItem[]; ok: boolean } => {
    const blocks = stackBlocks(ctx, scale);
    const bottom = format.safeTop + (format.stackTopRoom ?? 0) + stackHeight(blocks, gap);
    return { items: placeStack(blocks, gap, cta.top - gap - bottom, format), ok: bottom + gap <= cta.top + 0.01 };
  };
  let scale = 1;
  let result = fits(scale);
  if (!result.ok) {
    let low = MIN_SCALE;
    let high = 1;
    for (let step = 0; step < 20 && high - low > 0.005; step += 1) {
      const middle = (low + high) / 2;
      if (fits(middle).ok) {
        low = middle;
      } else {
        high = middle;
      }
    }
    scale = Math.floor(low * 1000) / 1000;
    result = fits(scale);
  }
  return { items: [...result.items, ...cta.items], frame, format, scale };
}
